import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  MenuBook as BookIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import { catalogErrorText } from './bookCatalogErrors';

const BOOK_API = `${config.API_BASE_URL}/api/v1/content/admin/books`;
const AUTHOR_API = `${config.API_BASE_URL}/api/v1/content/admin/authors`;
const WAREHOUSE_API = `${config.API_BASE_URL}/api/v1/content/admin/warehouses`;

const TEAM_API = `${config.API_BASE_URL}/em/v1/admin/teams`;

const defaultForm = {
  title: '',
  isbn: '',
  description: '',
  price: '',
  weightOz: '',
  thumbnailUrl: '',
  visibility: 'PAID',
  stockQuantity: '',
  isActive: true,
  authorIds: [],
  eligibleTeams: ['all'],
};

// For the loading functions the mount effect calls: the hook's `t` is for rendering.
const tBooks = (key, options) => i18n.t(key, { ns: 'books', ...options });

const BookManagementPage = () => {
  const { t } = useTranslation('books');
  const { getAccessToken } = useAuth();

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tBooks('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState('ALL');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);

  useEffect(() => {
    fetchBooks();
    fetchAuthors();
    fetchTeams();
  }, []);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const res = await fetch(BOOK_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error(tBooks('errors.fetchBooks'));
      const data = await res.json();
      setBooks(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(catalogErrorText(err, tBooks('errors.fetchBooks')));
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthors = async () => {
    try {
      const res = await fetch(AUTHOR_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch authors');
      const data = await res.json();
      setAuthors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching authors:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await fetch(TEAM_API, { headers: await authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch teams');
      const json = await res.json();
      const list = Array.isArray(json) ? json : (Array.isArray(json.data) ? json.data : []);
      setTeams(list);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchInventory = async (bookId) => {
    setInventoryLoading(true);
    try {
      const res = await fetch(`${BOOK_API}/${bookId}/inventory`, { headers: await authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch inventory');
      const data = await res.json();
      setInventory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  // For a brand-new book (no id yet), seed the editor with all active warehouses
  // at quantity 0. The quantities are saved right after the book is created.
  const loadBlankInventory = async () => {
    setInventoryLoading(true);
    try {
      const res = await fetch(`${WAREHOUSE_API}?activeOnly=true`, { headers: await authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch warehouses');
      const json = await res.json();
      const list = Array.isArray(json) ? json : (Array.isArray(json.data) ? json.data : []);
      setInventory(
        list
          .filter((w) => w.isActive !== false)
          .map((w) => ({ warehouseId: w.id, warehouseName: w.name, quantity: 0 }))
      );
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      setInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleInventoryChange = (warehouseId, value) => {
    setInventory((prev) =>
      prev.map((row) =>
        row.warehouseId === warehouseId ? { ...row, quantity: value } : row
      )
    );
  };

  // Upload a thumbnail image to S3 (same endpoint used for events, content, etc.)
  // and store the returned URL as the book's thumbnailUrl.
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-selected later
    if (!file) return;
    setUploadingThumb(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error(t('errors.authRequired'));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'book-thumbnails');
      const res = await fetch(`${config.API_BASE_URL}/em/v1/admin/images/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }, // no Content-Type: browser sets the multipart boundary
        body: formData,
      });
      if (!res.ok) throw new Error(t('errors.uploadFailed'));
      const json = await res.json();
      const imageUrl = json?.data?.imageUrl;
      if (!imageUrl) throw new Error(t('errors.noImageUrl'));
      setForm((prev) => ({ ...prev, thumbnailUrl: imageUrl }));
      toast.success(t('toast.thumbnailUploaded'));
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.uploadThumbnailFallback')));
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleTeamSelection = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      if (value === 'all') {
        return { ...prev, eligibleTeams: checked ? ['all'] : [] };
      }
      const filtered = prev.eligibleTeams.filter((t) => t !== 'all');
      return {
        ...prev,
        eligibleTeams: checked ? [...filtered, value] : filtered.filter((t) => t !== value),
      };
    });
  };

  const openCreate = () => {
    setIsEdit(false);
    setSelectedBook(null);
    setForm({ ...defaultForm });
    setInventory([]);
    loadBlankInventory();
    setDialogOpen(true);
  };

  const openEdit = (book) => {
    setIsEdit(true);
    setSelectedBook(book);
    setForm({
      title: book.title || '',
      isbn: book.isbn || '',
      description: book.description || '',
      price: book.price ?? '',
      weightOz: book.weightOz ?? '',
      thumbnailUrl: book.thumbnailUrl || '',
      visibility: book.visibility || 'PAID',
      stockQuantity: book.stockQuantity ?? '',
      isActive: book.isActive ?? true,
      authorIds: book.authors ? book.authors.map((a) => a.id) : book.authorIds || [],
      eligibleTeams: book.eligibleTeams || ['all'],
    });
    setInventory([]);
    fetchInventory(book.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error(t('errors.titleRequired'));
      return;
    }
    if (form.price === '' || isNaN(Number(form.price)) || Number(form.price) < 0) {
      toast.error(t('errors.priceRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        weightOz: form.weightOz !== '' ? parseFloat(form.weightOz) : null,
        stockQuantity: form.stockQuantity !== '' ? parseInt(form.stockQuantity, 10) : null,
        eligibleTeams: form.eligibleTeams.length > 0 ? form.eligibleTeams : ['all'],
      };
      const url = isEdit ? `${BOOK_API}/${selectedBook.id}` : BOOK_API;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: await authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          translateServerMessage(err.message, t(isEdit ? 'errors.updateFailed' : 'errors.createFailed'))
        );
      }

      // Persist per-warehouse inventory. On create we use the id returned from
      // the just-created book; on edit we use the existing book id.
      const savedBook = await res.json().catch(() => null);
      const bookId = isEdit ? selectedBook.id : (savedBook && savedBook.id);
      if (bookId && inventory.length > 0) {
        const invPayload = {
          inventory: inventory.map((row) => ({
            warehouseId: row.warehouseId,
            quantity: row.quantity === '' || row.quantity == null ? 0 : parseInt(row.quantity, 10),
          })),
        };
        const invRes = await fetch(`${BOOK_API}/${bookId}/inventory`, {
          method: 'PUT',
          headers: await authHeaders(),
          body: JSON.stringify(invPayload),
        });
        if (!invRes.ok) {
          const err = await invRes.json().catch(() => ({}));
          throw new Error(translateServerMessage(err.message, t('errors.inventoryFailed')));
        }
      }

      toast.success(t(isEdit ? 'toast.updated' : 'toast.created'));
      setDialogOpen(false);
      fetchBooks();
    } catch (err) {
      toast.error(catalogErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (book) => {
    if (!window.confirm(t('confirmDelete', { title: book.title }))) return;
    try {
      const res = await fetch(`${BOOK_API}/${book.id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      if (!res.ok) throw new Error(t('errors.deleteFailed'));
      toast.success(t('toast.deleted'));
      fetchBooks();
    } catch (err) {
      toast.error(catalogErrorText(err, t('errors.deleteFallback')));
    }
  };

  const getAuthorNames = (book) => {
    if (book.authors && book.authors.length > 0) {
      return book.authors.map((a) => a.name).join(', ');
    }
    return '—';
  };

  const filteredBooks = books.filter((book) => {
    if (search && !book.title?.toLowerCase().includes(search.toLowerCase())) return false;
    if (authorFilter !== 'ALL') {
      const authorId = parseInt(authorFilter, 10);
      const hasAuthor = book.authors?.some((a) => a.id === authorId);
      if (!hasAuthor) return false;
    }
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('header.title')}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t('header.subtitle')}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('header.refresh')}>
            <IconButton onClick={fetchBooks} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('header.add')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.author')}</InputLabel>
              <Select
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                label={t('filters.author')}
              >
                <MenuItem value="ALL">{t('filters.allAuthors')}</MenuItem>
                {authors.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="caption" color="text.secondary">
              <Trans
                t={t}
                i18nKey="filters.bookCount"
                count={filteredBooks.length}
                components={{ strong: <strong /> }}
              />
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.title')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.authors')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.isbn')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.price')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.visibility')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.stock')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.teams')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('table.status')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBooks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box py={4}>
                      <BookIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">{t('table.empty')}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBooks.map((book) => (
                  <TableRow key={book.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        {book.thumbnailUrl ? (
                          <img
                            src={book.thumbnailUrl}
                            alt={book.title}
                            style={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 4 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Box sx={{ width: 40, height: 56, borderRadius: 1, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BookIcon fontSize="small" color="action" />
                          </Box>
                        )}
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{book.title}</Typography>
                          {book.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {book.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getAuthorNames(book)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {book.isbn || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        ${Number(book.price || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`visibilityChip.${book.visibility || 'PAID'}`, {
                          defaultValue: book.visibility || 'PAID',
                        })}
                        size="small"
                        color={book.visibility === 'BUNDLE_ONLY' ? 'warning' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{book.stockQuantity ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {book.eligibleTeams?.includes('all') ? t('table.allTeams') : (book.eligibleTeams?.join(', ') || t('table.allTeams'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={book.isActive ? t('status.active') : t('status.inactive')}
                        color={book.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('table.edit')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(book)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('table.delete')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(book)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? t('dialog.editTitle') : t('dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('dialog.title')}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('dialog.isbn')}
                  value={form.isbn}
                  onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('dialog.price')}
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><MoneyIcon /></InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('dialog.description')}
                  multiline
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('dialog.weight')}
                  type="number"
                  value={form.weightOz}
                  onChange={(e) => setForm({ ...form, weightOz: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t('dialog.visibility')}</InputLabel>
                  <Select
                    value={form.visibility}
                    onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                    label={t('dialog.visibility')}
                  >
                    <MenuItem value="PAID">{t('visibilityOption.PAID')}</MenuItem>
                    <MenuItem value="BUNDLE_ONLY">{t('visibilityOption.BUNDLE_ONLY')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('dialog.stockQuantity')}
                  type="number"
                  value={form.stockQuantity}
                  onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label={t('dialog.thumbnailUrl')}
                    placeholder={t('dialog.thumbnailPlaceholder')}
                    value={form.thumbnailUrl}
                    onChange={(e) => setForm({ ...form, thumbnailUrl: e.target.value })}
                  />
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={uploadingThumb ? <CircularProgress size={16} /> : <UploadIcon />}
                    disabled={uploadingThumb}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap', height: 56, flexShrink: 0 }}
                  >
                    {uploadingThumb ? t('dialog.uploading') : t('dialog.upload')}
                    <input hidden type="file" accept="image/*" onChange={handleThumbnailUpload} />
                  </Button>
                  {form.thumbnailUrl ? (
                    <Box
                      component="img"
                      key={form.thumbnailUrl}
                      src={form.thumbnailUrl}
                      alt={t('dialog.thumbnailPreview')}
                      sx={{
                        width: 56, height: 56, objectFit: 'cover', borderRadius: 1,
                        border: '1px solid', borderColor: 'divider', flexShrink: 0,
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : null}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ minWidth: 220 }}>
                  <Select
                    multiple
                    displayEmpty
                    value={form.authorIds}
                    onChange={(e) => setForm({ ...form, authorIds: e.target.value })}
                    renderValue={(selected) =>
                      selected && selected.length > 0
                        ? selected
                            .map((id) => authors.find((a) => a.id === id)?.name || id)
                            .join(', ')
                        : <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('dialog.author')}</span>
                    }
                  >
                    {authors.map((author) => (
                      <MenuItem key={author.id} value={author.id}>
                        {author.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('dialog.eligibleTeams')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="all"
                        checked={form.eligibleTeams.includes('all')}
                        onChange={handleTeamSelection}
                      />
                    }
                    label={t('dialog.allTeams')}
                  />
                  {teams.map((team) => (
                    <FormControlLabel
                      key={team.id || team.name}
                      control={
                        <Checkbox
                          value={team.name}
                          checked={form.eligibleTeams.includes(team.name)}
                          onChange={handleTeamSelection}
                          disabled={form.eligibleTeams.includes('all')}
                        />
                      }
                      label={team.name}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('dialog.inventoryTitle')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('dialog.inventoryHelp')}
                </Typography>
                  {inventoryLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : inventory.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('dialog.noWarehouses')}
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>{t('dialog.warehouse')}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} width={140}>{t('dialog.quantity')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventory.map((row) => (
                            <TableRow key={row.warehouseId}>
                              <TableCell>{row.warehouseName}</TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={row.quantity ?? 0}
                                  onChange={(e) => handleInventoryChange(row.warehouseId, e.target.value)}
                                  inputProps={{ min: 0 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={form.isActive ? t('status.active') : t('status.inactive')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('dialog.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('dialog.saving') : isEdit ? t('dialog.update') : t('dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookManagementPage;
